import Button from "@edaz/edazkit/Button";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import type { ViewComponent } from "@frui.ts/views";
import { View, registerView } from "@frui.ts/views";
import CustomerRefundType from "entities/customerRefundType";
import { observer } from "mobx-react-lite";
import React from "react";
import { Col, Modal, Row } from "react-bootstrap";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import CustomerRefundCandidateDetailViewModel from "viewModels/finance/customerRefunds/customerRefundCandidates/customerRefundCandidateDetailViewModel";
import PrevNextNavigationHeader from "views/common/prevNextNavigationHeader";
import OrderSection from "../../common/orderSection";
import OriginalPayGatePaymentSection from "../../common/originalPayGatePaymentSection";
import OriginalTransferPaymentSection from "../../common/originalTransferPaymentSection";
import RefundSection from "./refundSection";
import classNames from "classnames";
import Tooltip from "@edaz/edazkit/Tooltip/index";
import ChangesDuringValiditySection from "./changesDuringValiditySection";
import FuelTypeSection from "../../common/fuelTypeSection";

// eslint-disable-next-line sonarjs/cognitive-complexity
export const CustomerRefundCandidateDetailView: ViewComponent<CustomerRefundCandidateDetailViewModel> = observer(({ vm }) => {
  const refundAllowed = vm.isUserAllowedToEdit ? vm.item?.permittedActions.refundation.isAllowed : false;
  const rejectionReason = vm.item?.permittedActions.refundation.rejectionReason;

  // We need to fake disabled state, because Bootstrap doesn't propagate events on disabled buttons
  const RefundButton = ({ disabled }: { disabled?: boolean }) => {
    const refundOnClick = disabled ? () => null : vm.onGenerateRefundClick;
    return (
      <Button
        variant="primary"
        disabled={vm.busyWatcher.isBusy}
        className={classNames("text-uppercase", { disabled })}
        onClick={refundOnClick}>
        {vm.translate(vm.item?.refundType === CustomerRefundType.CustomerPartial ? "generateAndSubmit" : "generateRefund")}
      </Button>
    );
  };
  const RejectButton = ({ disabled }: { disabled?: boolean }) => {
    const rejectOnClick = disabled ? () => null : vm.onRejectionClick;
    return (
      <Button
        variant="danger"
        disabled={vm.busyWatcher.isBusy}
        className={classNames("text-uppercase", { disabled })}
        onClick={rejectOnClick}>
        {vm.translate("reject")}
      </Button>
    );
  };

  return (
    <Modal scrollable show={true} centered={true} onHide={vm.requestClose} dialogClassName="modal-90w">
      <Modal.Header closeButton={true}>
        <PrevNextNavigationHeader vm={vm} />
        <ButtonToolbar className="flex-grow-1 justify-content-end">
          {vm.isUserAllowedToEdit ? (
            <RejectButton />
          ) : (
            <Tooltip
              tooltip={<div style={{ padding: "5px 0" }}>{vm.translate("rejection.undefined")}</div>}
              trigger={["hover", "focus"]}
              id="rejection-detail">
              <div className="kit__button">
                <RejectButton disabled={true} />
              </div>
            </Tooltip>
          )}
          {refundAllowed ? (
            <RefundButton />
          ) : (
            <Tooltip
              tooltip={<div style={{ padding: "5px 0" }}>{vm.translate(`rejection.${rejectionReason}`)}</div>}
              trigger={["hover", "focus"]}
              id="rejection-detail">
              <div className="kit__button">
                <RefundButton disabled={true} />
              </div>
            </Tooltip>
          )}
        </ButtonToolbar>
      </Modal.Header>
      <Modal.Body>
        {vm.item ? (
          <LoaderOverlay loading={vm.busyWatcher.isBusy}>
            <View vm={vm.alertsViewModel} useLifecycle={true} />
            <Row className="mb-4">
              <Col>
                <OrderSection vm={vm} />
              </Col>
            </Row>
            {vm.item.charge.chargeChangesDuringValidity && vm.item.charge.chargeChangesDuringValidity.length > 0 && (
              <Row className="mb-4">
                <Col>
                  <ChangesDuringValiditySection vm={vm} />
                </Col>
              </Row>
            )}
            <Row>
              <Col md={6}>
                {vm.item.originalPayGatePayment || vm.isPayGateRefund ? (
                  <OriginalPayGatePaymentSection vm={vm} />
                ) : (
                  <OriginalTransferPaymentSection vm={vm} />
                )}
              </Col>
              <Col md={6}>
                {vm.item.requestedPriceListItemId && vm.item.refundType === CustomerRefundType.CustomerPartial && (
                  <FuelTypeSection vm={vm} />
                )}
                <RefundSection vm={vm} />
              </Col>
            </Row>
          </LoaderOverlay>
        ) : (
          <LoaderOverlay loading={true} style={{ minHeight: 200 }} />
        )}
      </Modal.Body>
    </Modal>
  );
});

registerView(CustomerRefundCandidateDetailView, CustomerRefundCandidateDetailViewModel);
